import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, CheckCircle2 } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const inputCls = "w-full px-3 py-2 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/20";
const labelCls = "block text-xs font-semibold text-muted-foreground mb-1";

const EMPTY = { name: "", email: "", phone: "", address: "", city: "", state: "", position: "", licensed_states: "" };

export function CareTeamInquiryDialog({ open, onClose }) {
    const [f, setF] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [done, setDone] = useState(false);
    const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

    const submit = async () => {
        if (!f.name || !f.email || !f.phone || !f.position || !f.licensed_states) {
            toast.error("Please fill name, email, phone, position and licensed states.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/care-team-inquiry", f);
            setDone(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not submit inquiry. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const close = () => { setF(EMPTY); setDone(false); onClose(); };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && close()}>
            <DialogContent className="max-w-lg" data-testid="care-team-inquiry-dialog">
                {done ? (
                    <div className="py-6 text-center space-y-3">
                        <CheckCircle2 className="w-12 h-12 text-success mx-auto" />
                        <h3 className="font-display font-bold text-xl text-phoenix-navy">Thank you!</h3>
                        <p className="text-sm text-muted-foreground">Your inquiry has been sent to our partnerships team. We'll reach out shortly.</p>
                        <button data-testid="inquiry-done" onClick={close} className="mt-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-phoenix-navy text-white">Close</button>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-phoenix-orange" /> Join our care team</DialogTitle>
                            <p className="text-sm text-muted-foreground">Tell us a bit about you — we'll be in touch about provider & therapist opportunities.</p>
                        </DialogHeader>
                        <div className="grid grid-cols-2 gap-3 max-h-[55vh] overflow-y-auto pr-1">
                            <div className="col-span-2"><label className={labelCls}>Full name *</label><input data-testid="inquiry-name" className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} /></div>
                            <div><label className={labelCls}>Email *</label><input data-testid="inquiry-email" type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} /></div>
                            <div><label className={labelCls}>Phone *</label><input data-testid="inquiry-phone" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} /></div>
                            <div className="col-span-2"><label className={labelCls}>Address</label><input className={inputCls} value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
                            <div><label className={labelCls}>City</label><input className={inputCls} value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
                            <div><label className={labelCls}>State</label><input className={inputCls} value={f.state} onChange={(e) => set("state", e.target.value)} /></div>
                            <div className="col-span-2"><label className={labelCls}>Position interested in *</label><input data-testid="inquiry-position" className={inputCls} value={f.position} onChange={(e) => set("position", e.target.value)} placeholder="e.g. Therapist (LCSW), Psychiatric NP, Primary Care" /></div>
                            <div className="col-span-2"><label className={labelCls}>Licensed states *</label><input data-testid="inquiry-states" className={inputCls} value={f.licensed_states} onChange={(e) => set("licensed_states", e.target.value)} placeholder="e.g. NM, AZ, TX" /></div>
                        </div>
                        <DialogFooter>
                            <button onClick={close} className="px-4 py-2 rounded-lg text-sm border border-border">Cancel</button>
                            <button data-testid="inquiry-submit" onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-phoenix-navy text-white disabled:opacity-50">
                                {saving ? "Sending…" : "Submit inquiry"}
                            </button>
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
